import React, {useEffect, useState} from "react";
import {Alert, Backdrop, Button, CircularProgress, Container, Pagination, Paper, styled} from "@mui/material";
import ExternalSourceBreadcrumbs from "../ExternalSourceManagement/ExternalSourceBreadcrumbs";
import {Link, useLocation, useParams} from "react-router-dom";
import Grid from "@mui/material/Grid";
import AddIcon from "@mui/icons-material/Add";
import {ApiUrl, PortalUrl} from "../utils/portal-urls";
import {getQueryParams, handleHttpResponse} from "../utils/http-helpers";
import RefreshIcon from "@mui/icons-material/Refresh";
import ShowSnackbar from "../utils/ShowSnackbar";
import {getAllBatches} from "../features/allBatchesSlice";
import {useDispatch, useSelector} from "react-redux";
import {batchCurrentStatus} from "../ExternalSourceManagement/ExternalSourceManagementHome";
import ExternalSourceBatchProgressBar from "../ExternalSourceManagement/ExternalSourceBatchProgressBar";
import TerminologyLogsTable from "./TerminologyLogsTable";
import {lookupTerminology} from "../utils/terminology";

/**
 * Import history and live progress for any Amendment #1 A5 value set; which one comes from the
 * :name route param.
 *
 * @date: 08/11/26
 * @copy: (C) Copyright 2026 MaxMD Corporation, All Rights Reserved.
 */

const StyledContainer = styled(Container)(({theme}) => ({
    paddingTop: theme.spacing(4),
    paddingBottom: theme.spacing(4),
}))
const StyledPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
    boxShadow: '0 7px 20px #cccccc',
    minHeight: 450,
    borderRadius: '15px',
}))
const StyledBatchDiv = styled('div')({
    marginBottom: '20px',
    '&:last-child': {marginBottom: 0},
})
const StyledDiv = styled('div')({
    display: "flex",
    float: "right",
})
const StyledBatchPaper = styled(Paper)(({theme}) => ({
    padding: theme.spacing(2),
}))

export default function TerminologyHome() {
    const location = useLocation();
    const {name} = useParams();
    const terminology = lookupTerminology(name);

    const getParameters = getQueryParams();
    const [pageResult, setPageResult] = useState(null);
    const [loading, setLoading] = useState(false);
    const [snackbarMsg, setSnackbarMsg] = useState({open: false, msg: '', msgLv: ''});
    const [page, setPage] = useState(getParameters.page ? parseInt(getParameters.page) : 0);
    const [refresh, setRefresh] = useState(new Date().getTime());

    const dispatch = useDispatch();
    const {terminologyBatches, batchRefresh} = useSelector(state => state.allBatches);
    const batches = (terminology && terminologyBatches[terminology.key]) || [];

    useEffect(() => {
        if (!terminology) {
            return;
        }
        fetchLogsPage(terminology.source, page);
        dispatch(getAllBatches());
    }, [page, refresh, batchRefresh, name])

    const fetchLogsPage = async (source, page) => {
        setLoading(true);
        const url = ApiUrl.ExternalSource.getExternalSourceLogs.getPath(source, "", page);
        try {
            const res = await fetch(url);
            setPageResult(await handleHttpResponse(res));
        } catch (err) {
            console.error(`Failed to load ${source} logs, ` + err);
            setSnackbarMsg({open: true, msg: `Failed to load ${source} logs`, msgLv: 'error'});
        } finally {
            setLoading(false);
        }
    }

    if (!terminology) {
        return (
            <StyledContainer maxWidth='xl'>
                <Alert severity='error'>Unknown value set "{name}".</Alert>
            </StyledContainer>
        )
    }

    return (
        <StyledContainer maxWidth='xl'>
            <ExternalSourceBreadcrumbs path={location.pathname}/>
            <h3 className='title'>
                {terminology.label}
                <StyledDiv>
                    <Button variant="outlined" component={Link}
                            to={PortalUrl.ExternalSource.Terminology.getNewTask(terminology.key)} endIcon={<AddIcon/>}>
                        New Import Task
                    </Button>
                    <Button variant="outlined" onClick={() => setRefresh(new Date().getTime())} endIcon={<RefreshIcon/>}>Refresh</Button>
                </StyledDiv>
            </h3>

            <Grid container spacing={3}>
                <Grid item xs={12} md={12} lg={12}>
                    <h4>{terminology.label} Logs</h4>
                    <StyledPaper>
                        {pageResult?.content?.length > 0 ?
                            <div>
                                <TerminologyLogsTable logs={pageResult.content}/>
                                <br/>
                                {pageResult.totalPages >= 1 &&
                                    <Pagination page={page + 1} count={pageResult.totalPages}
                                                variant='outlined' color='primary'
                                                onChange={(e, newPage) => setPage(newPage - 1)}/>}
                            </div>
                            : <Alert severity='info' icon={false}>This value set has never been imported.</Alert>
                        }
                    </StyledPaper>
                </Grid>
                <Grid item xs={12} md={12} lg={12} sx={{mt: '25px'}}>
                    <h4>Current {terminology.label} Progress Information</h4>
                    {batches.length > 0 ?
                        batches.map(b => (
                            <StyledBatchDiv key={b.id}>
                                <StyledBatchPaper>
                                    {b.status &&
                                        <Alert severity={batchCurrentStatus[b.status].getStatus} icon={false}>
                                            {batchCurrentStatus[b.status].getMsg(`${terminology.label} Import Task`)}
                                        </Alert>}
                                    <br/>
                                    {b.id && b.status !== 'Completed' && b.status !== 'Cancelled' && b.status !== 'Failed' &&
                                        <ExternalSourceBatchProgressBar batch={b}/>}
                                </StyledBatchPaper>
                            </StyledBatchDiv>
                        )) :
                        <StyledPaper sx={{minHeight: '200px'}}>
                            <Alert severity='info' icon={false}>No Active Process</Alert>
                        </StyledPaper>
                    }
                </Grid>
            </Grid>
            <ShowSnackbar open={snackbarMsg.open} close={(c) => setSnackbarMsg(c)} msg={snackbarMsg}/>
            <Backdrop sx={{zIndex: theme => theme.zIndex.drawer + 1, color: "#fff"}} open={loading}>
                <CircularProgress color={"inherit"}/>
            </Backdrop>
        </StyledContainer>
    )
}
